import { useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import { DOWNLOAD } from "@/constants/testIds";
import { Apple, Play, Check, Loader2, Rocket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DownloadCTA = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const res = await axios.post(`${API}/waitlist`, { email, source: "landing" });
      setStatus("success");
      setMessage(res.data?.message || "You're on the list!");
      setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      setMessage(msg || "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="download"
      data-testid={DOWNLOAD.section}
      className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
    >
      <div className="grid gap-6 lg:grid-cols-2">
        {/* App store CTA */}
        <Reveal>
          <div
            className="relative h-full overflow-hidden rounded-3xl border border-border/70 bg-card/70 p-8 shadow-[var(--shadow-elev-2)] backdrop-blur-md sm:p-10"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(700px circle at 20% 10%, rgba(0,229,255,0.12), transparent 55%)" }}
            />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-neo-cyan">
                <Rocket className="h-3.5 w-3.5" /> Launching soon
              </span>
              <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                One app to print it &mdash; and sell it.
              </h2>
              <p className="mt-3 max-w-sm text-base text-muted-foreground">
                Discover designs, order prints, upload your own models and track
                everything in one place. Now on iOS &mdash; Android coming soon.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  data-testid={DOWNLOAD.appStore}
                  href="https://apps.apple.com/us/app/printopia/id6788813110"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative inline-flex items-center gap-3 rounded-xl border border-[rgba(0,229,255,0.35)] bg-black px-5 py-3 text-left transition-[box-shadow,transform] duration-200 hover:shadow-[var(--glow-cyan)] active:scale-[0.98]"
                >
                  <Apple className="h-7 w-7 text-white" />
                  <span className="leading-tight">
                    <span className="block font-mono text-[10px] uppercase text-muted-foreground">Download on the</span>
                    <span className="block font-display text-base font-semibold text-white">App Store</span>
                  </span>
                </a>
                <button
                  data-testid={DOWNLOAD.googlePlay}
                  type="button"
                  className="group relative inline-flex items-center gap-3 rounded-xl border border-border/70 bg-black px-5 py-3 text-left transition-[box-shadow,transform] duration-200 hover:shadow-[var(--glow-magenta)] active:scale-[0.98]"
                >
                  <span className="absolute -right-2 -top-2 rounded-full border border-[rgba(212,0,255,0.5)] bg-black px-2 py-0.5 font-mono text-[9px] uppercase tracking-wide text-neo-magenta">
                    Soon
                  </span>
                  <Play className="h-6 w-6 text-white" />
                  <span className="leading-tight">
                    <span className="block font-mono text-[10px] uppercase text-muted-foreground">Get it on</span>
                    <span className="block font-display text-base font-semibold text-white">Google Play</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Waitlist form */}
        <Reveal delay={0.08}>
          <div className="relative h-full overflow-hidden rounded-3xl border border-border/70 bg-card/70 p-8 shadow-[var(--shadow-elev-1)] backdrop-blur-md sm:p-10">
            <h3 className="font-display text-2xl font-semibold tracking-tight">
              Be first in line
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Join the waitlist for early access &mdash; we'll let you know the
              moment Printopia goes live.
            </p>

            {status === "success" ? (
              <div
                data-testid={DOWNLOAD.success}
                className="mt-6 flex items-start gap-3 rounded-2xl border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] p-4"
              >
                <Check className="mt-0.5 h-5 w-5 text-neo-cyan" />
                <p className="text-sm text-foreground">{message}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-3">
                <label htmlFor="waitlist-email" className="block text-sm font-medium text-foreground">
                  Email address
                </label>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    id="waitlist-email"
                    data-testid={DOWNLOAD.email}
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-12 flex-1 rounded-[var(--radius)] border border-border/70 bg-background/50 px-4 text-sm text-foreground outline-none transition-colors duration-200 placeholder:text-muted-foreground focus:border-[rgba(0,229,255,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(0,229,255,0.35)]"
                  />
                  <button
                    data-testid={DOWNLOAD.submit}
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-[var(--radius)] border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.16)] px-5 text-sm font-medium text-foreground shadow-[var(--glow-cyan)] transition-[background-color,transform] duration-200 hover:bg-[rgba(0,229,255,0.24)] active:scale-[0.98] disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Joining…</>
                    ) : (
                      "Join Waitlist"
                    )}
                  </button>
                </div>
                {status === "error" && (
                  <p data-testid={DOWNLOAD.error} className="text-xs text-red-300">
                    {message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  No spam, ever. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
